import { createFeatureSelector, createSelector } from '@ngrx/store';
import { FloorAclState } from './floor-acl.state';
import { selectCurrentAclId } from '../router/acl-router.selector';
import { selectFloorId } from '../router/locations-router.selector';
import { FloorAclMembers, FloorAclGroups, FloorAcl } from '@era/locations-sdk';
import * as groupSelector from '../group/group.selector';
import * as fromFloorAcl from './floor-acl.reducer';
import * as userSelector from '../user/user.selector';
import { User, Group } from '@era/security-sdk';
import {
  NamedLocationAcl,
  NamedLocationAclGroups,
  NamedLocationAclMembers,
} from '../location-acl/location-acl.selector';

export interface NamedFloorAclMembers extends FloorAclMembers {
  name: string;
}

export interface NamedFloorAclGroups extends FloorAclGroups {
  name: string;
}

export interface NamedFloorAcl {
  resourceId?: string;
  groups?: NamedFloorAclGroups[];
  users?: NamedFloorAclMembers[];
  locationAcl?: NamedLocationAcl;
}

export const selectFloorAclState = createFeatureSelector<FloorAclState>('floorAcls');

export const selectFloorAclIds = createSelector(selectFloorAclState, fromFloorAcl.selectFloorAclIds);

export const selectAllFloorAcls = createSelector(selectFloorAclState, fromFloorAcl.selectAllFloorAcls);

export const selectFloorAcls = createSelector(selectFloorAclState, fromFloorAcl.selectFloorAcls);

export const selectFloorAclLoaded = createSelector(
  selectAllFloorAcls,
  (floorAcls: FloorAcl[]) => floorAcls && floorAcls.length > 0
);

export const selectFloorAclByResource = (resourceId: string) =>
  createSelector(selectAllFloorAcls, (floorAcls: FloorAcl[]) =>
    floorAcls ? floorAcls.find((x) => x.resourceId === resourceId) : undefined
  );

export const selectRoomAclByResourceWithName = (resourceId: string) =>
  createSelector(
    selectAllFloorAcls,
    userSelector.selectAllUsers,
    groupSelector.selectAllGroups,
    (floorAcls: FloorAcl[], users: User[], groups: Group[]) => {
      const findUserName = (id: string) => {
        const user = users.find((x) => x.id === id);
        return user ? user.displayName : undefined;
      };

      const findGroupName = (id: string) => {
        const group = groups.find((x) => x.id === id);
        return group ? group.displayName : undefined;
      };

      if (floorAcls && users && groups) {
        const acl = floorAcls.find((x) => x.resourceId === resourceId);
        if (acl) {
          const floorAclGroups: NamedFloorAclGroups[] = acl.groups.map((x) => ({
            ...x,
            name: findGroupName(x.groupId),
          }));
          const floorAclMembers: NamedFloorAclMembers[] = acl.users.map((x) => ({
            ...x,
            name: findUserName(x.member),
          }));
          const locationAclGroups: NamedLocationAclGroups[] = acl.locationAcl.groups.map((x) => ({
            ...x,
            name: findGroupName(x.groupId),
          }));
          const locationAclMembers: NamedLocationAclMembers[] = acl.locationAcl.users.map((x) => ({
            ...x,
            name: findUserName(x.member),
          }));

          const floorAcl: NamedFloorAcl = {
            ...acl,
            groups: floorAclGroups,
            users: floorAclMembers,
            locationAcl: {
              ...acl.locationAcl,
              groups: locationAclGroups,
              users: locationAclMembers,
            },
          };

          return floorAcl;
        }
      }
      return undefined;
    }
  );

export const selectCurrentAclGroupsWithNamesByGroupIds = (groupIds: string[]) =>
  createSelector(
    selectFloorAcls,
    selectFloorId,
    groupSelector.selectAllGroups,
    (floorAcls, floorId: string, groups: Group[]) => {
      if (floorAcls && floorId && groupIds) {
        const findGroupName = (id: string) => {
          const group = groups.find((x) => x.id === id);
          return group ? group.displayName : undefined;
        };

        const selectedAcl = floorAcls[floorId];
        const floorAclMembers = selectedAcl.groups.filter((x) => groupIds.includes(x.groupId));

        return floorAclMembers ? floorAclMembers.map((x) => ({ ...x, name: findGroupName(x.groupId) })) : undefined;
      }
      return undefined;
    }
  );

export const selectCurrentAclGroup = createSelector(
  selectFloorAcls,
  selectCurrentAclId,
  selectFloorId,
  (userGroupAcls, aclGroupId: string, floorId: string) => {
    if (userGroupAcls && aclGroupId) {
      const selectedAcl = userGroupAcls[floorId];
      return selectedAcl ? selectedAcl.groups.find((x) => x.groupId === aclGroupId) : undefined;
    }
    return undefined;
  }
);

export const selectCurrentAclMember = createSelector(
  selectFloorAcls,
  selectCurrentAclId,
  selectFloorId,
  (userGroupAcls, aclUserId: string, floorId: string) => {
    if (userGroupAcls && aclUserId) {
      // No group? Must be group wide.
      const selectedAcl = userGroupAcls[floorId];
      return selectedAcl ? selectedAcl.users.find((x) => x.member === aclUserId) : undefined;
    }
    return undefined;
  }
);
